import { initializeSocket } from "../app/profile/utils/socketUtils";
import getTurnCredentials from "../api/getTurnCredentials";

// Base ICE servers. STUN is enough on open networks; symmetric NAT (most
// mobile carriers, corporate Wi-Fi) needs a TURN relay. Static TURN is opt-in
// via env; dynamic credentials from the backend are merged on top at call
// setup (see resolveIceServers) so they can be rotated without a rebuild.
const RTC_ICE_SERVERS = [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    ...(process.env.REACT_APP_TURN_URL
        ? [{
              urls: process.env.REACT_APP_TURN_URL,
              username: process.env.REACT_APP_TURN_USERNAME || "",
              credential: process.env.REACT_APP_TURN_CREDENTIAL || "",
          }]
        : []),
];

// STUN servers never need auth, so they're always kept from the base list.
const STUN_ONLY = RTC_ICE_SERVERS.filter((s) => !s.username && !s.credential);

// Resolve the full iceServers list for a call: STUN (always) + TURN from the
// backend endpoint if available, otherwise the env-based TURN from the base
// list. Called once per peer connection setup.
async function resolveIceServers() {
    const remote = await getTurnCredentials();
    if (remote && remote.length) {
        return [...STUN_ONLY, ...remote];
    }
    return RTC_ICE_SERVERS;
}

const CALL_EVENTS = [
    "call:incoming",
    "call:invite_ack",
    "call:accepted",
    "call:rejected",
    "call:hungup",
    "call:peer_offer",
    "call:peer_answer",
    "call:peer_ice",
    "call:error",
];

// Which socket instance already has our listeners bound. Swaps when the
// singleton in socketUtils is recreated after login.
let listenersFor = null;

function attachSocketListeners(socket, handlers) {
    if (listenersFor === socket) return;
    if (listenersFor) {
        CALL_EVENTS.forEach((name) => listenersFor.off(name));
    }
    socket.on("call:incoming", handlers.onIncoming);
    socket.on("call:invite_ack", handlers.onInviteAck);
    socket.on("call:accepted", handlers.onAccepted);
    socket.on("call:rejected", handlers.onRejected);
    socket.on("call:hungup", handlers.onHungup);
    socket.on("call:peer_offer", handlers.onPeerOffer);
    socket.on("call:peer_answer", handlers.onPeerAnswer);
    socket.on("call:peer_ice", handlers.onPeerIce);
    socket.on("call:error", handlers.onError);
    listenersFor = socket;
}

// Always fetch the current socket, then make sure listeners are wired.
// initializeSocket() returns the auth-ready singleton — before login it
// might be a stale/disconnected instance; grabbing it fresh every emit
// keeps us in sync with socketUtils.
function currentSocket(handlers) {
    const s = initializeSocket();
    if (handlers) attachSocketListeners(s, handlers);
    return s;
}

export function createVoiceCallController(handlers) {
    // Bind listeners to whatever socket exists right now.
    currentSocket(handlers);

    // Re-bind whenever socketUtils reconnects — after login, or after a
    // network drop, `socket:connected` fires and the singleton may have
    // been replaced. If we don't rebind, incoming calls silently vanish.
    const rebind = () => currentSocket(handlers);
    window.addEventListener("socket:connected", rebind);
    window.addEventListener("socket:reconnected", rebind);

    let pc = null;
    let localStream = null;
    // Who initiated the SDP negotiation. The caller creates the offer and is
    // the only side allowed to drive an ICE restart (re-offer). The callee
    // (answerer) waits for the offerer's re-offer.
    let isOfferer = false;
    // Set by teardown; checked after the async gap in invite() so a hangup
    // mid-invite can't emit a stray call_invite (phantom ring on the callee).
    let aborted = false;
    // Trickle-ICE candidates can land before setRemoteDescription finishes
    // (callee still in getUserMedia/pc setup, or offerer hasn't applied the
    // answer yet). addIceCandidate then throws — or pc is null — and the
    // candidate is lost, a classic cause of one-way audio. Buffer until the
    // remote description is set, then drain.
    let pendingIce = [];

    async function _ensureLocalStream() {
        if (localStream) return localStream;
        localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        const tracks = localStream.getAudioTracks();
        console.log("[voice_call] local mic acquired, tracks:", tracks.length, tracks.map((t) => ({ enabled: t.enabled, muted: t.muted, ready: t.readyState })));
        return localStream;
    }

    function _wirePeerConnection(conn, callId) {
        conn.onicecandidate = (event) => {
            if (event.candidate) {
                currentSocket().emit("call_ice_candidate", { call_id: callId, candidate: event.candidate });
            }
        };
        conn.ontrack = (event) => {
            handlers.onRemoteStream?.(event.streams[0]);
        };
        conn.onconnectionstatechange = () => {
            handlers.onConnectionStateChange?.(conn.connectionState);
        };
    }

    async function _newPeerConnection(callId) {
        const iceServers = await resolveIceServers();
        const conn = new RTCPeerConnection({ iceServers });
        _wirePeerConnection(conn, callId);
        return conn;
    }

    // Apply any ICE candidates that arrived before the remote description was
    // set. Called right after each setRemoteDescription succeeds.
    async function _drainIce() {
        if (!pc) return;
        const queued = pendingIce;
        pendingIce = [];
        for (const c of queued) {
            try {
                await pc.addIceCandidate(new RTCIceCandidate(c));
            } catch (err) {
                console.warn("[voice_call] failed to add queued ICE candidate", err);
            }
        }
    }

    // Serialize SDP state changes. Duplicate/stale signals delivered back-to-back
    // (socket redelivery, an answer to an ICE-restart re-offer racing the answer
    // to the original offer) would otherwise both pass the signalingState guard
    // while the first setRemoteDescription is still in flight, and the loser
    // throws "wrong state: stable".
    let sdpChain = Promise.resolve();
    function _serializeSdp(fn) {
        const run = sdpChain.then(fn);
        sdpChain = run.catch(() => {});
        return run;
    }

    return {
        async invite(toUserId) {
            aborted = false;
            await _ensureLocalStream();
            if (aborted) return; // teardown ran during getUserMedia — don't emit a stray invite
            const s = currentSocket(handlers);
            console.debug("[voice_call] invite", { to_user_id: toUserId, connected: s.connected, id: s.id });
            s.emit("call_invite", { to_user_id: toUserId });
        },

        async createAndSendOffer(callId, { iceRestart = false } = {}) {
            const stream = await _ensureLocalStream();
            // Reuse an existing pc for renegotiation (e.g. an ICE restart
            // re-offer) instead of throwing away the connection mid-call.
            if (!pc) {
                isOfferer = true;
                pc = await _newPeerConnection(callId);
                stream.getTracks().forEach((t) => pc.addTrack(t, stream));
            }
            const offer = await pc.createOffer({ offerToReceiveAudio: true, iceRestart });
            await pc.setLocalDescription(offer);
            currentSocket().emit("call_offer", { call_id: callId, sdp: offer });
        },

        async handlePeerOffer(callId, sdp) {
            await _serializeSdp(async () => {
                const stream = await _ensureLocalStream();
                // Reuse an existing pc when the offerer re-sends an offer (ICE
                // restart) — the answer is produced on the same connection.
                if (!pc) {
                    isOfferer = false;
                    pc = await _newPeerConnection(callId);
                    stream.getTracks().forEach((t) => pc.addTrack(t, stream));
                }
                await pc.setRemoteDescription(new RTCSessionDescription(sdp));
                await _drainIce();
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                currentSocket().emit("call_answer", { call_id: callId, sdp: answer });
            });
        },

        async handlePeerAnswer(sdp) {
            await _serializeSdp(async () => {
                if (!pc) return;
                // Only apply an answer while we have a pending local offer. In
                // "stable" the exchange already completed — this is a stale or
                // redelivered answer (e.g. an answer to a pre-ICE-restart offer
                // landing after the restart's answer, or a duplicate peer_answer),
                // and setRemoteDescription would throw "wrong state: stable".
                if (pc.signalingState !== "have-local-offer") {
                    console.warn("[voice_call] ignoring stale/duplicate answer (signalingState=" + pc.signalingState + ")");
                    return;
                }
                await pc.setRemoteDescription(new RTCSessionDescription(sdp));
                await _drainIce();
            });
        },

        async handlePeerIce(candidate) {
            if (!candidate) return;
            // Buffer until the peer connection exists AND has a remote
            // description; otherwise addIceCandidate throws and the candidate
            // is lost (one-way audio). Drained after setRemoteDescription.
            if (!pc || !pc.remoteDescription) {
                pendingIce.push(candidate);
                return;
            }
            try {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
            } catch (err) {
                console.warn("[voice_call] failed to add ICE candidate", err);
            }
        },

        accept(callId) {
            currentSocket().emit("call_accept", { call_id: callId });
        },
        // Only signals the rejection — no teardown. Callers own teardown, so
        // declining a queued second call can't kill an active call's media.
        reject(callId, reason) {
            currentSocket().emit("call_reject", { call_id: callId, reason });
        },
        hangup(callId) {
            currentSocket().emit("call_hangup", { call_id: callId });
            this.teardown();
        },

        // Drive an ICE restart. Only the offerer may re-offer; the answerer
        // returns false so the caller can fall back to waiting for the
        // offerer's re-offer. Returns a synchronous boolean — the caller gates
        // its fallback-timer decision on this value, so we must not await the
        // re-offer here. The re-offer itself is fire-and-forget.
        restartIce(callId) {
            if (!pc || !isOfferer) return false;
            try {
                pc.restartIce();
                this.createAndSendOffer(callId, { iceRestart: true }).catch((err) => {
                    console.warn("[voice_call] ICE restart re-offer failed", err);
                });
                return true;
            } catch (err) {
                console.warn("[voice_call] ICE restart failed", err);
                return false;
            }
        },

        setMuted(muted) {
            const enable = !muted;
            let applied = false;
            // Drive the senders directly — the sent track is what the peer
            // hears, and getSenders() stays valid even if the local stream
            // reference is momentarily stale.
            if (pc) {
                pc.getSenders().forEach((sender) => {
                    if (sender.track && sender.track.kind === "audio") {
                        sender.track.enabled = enable;
                        applied = true;
                    }
                });
            }
            if (localStream) {
                localStream.getAudioTracks().forEach((track) => {
                    track.enabled = enable;
                    applied = true;
                });
            }
            if (!applied) {
                console.warn("[voice_call] setMuted: no audio track available (call not ready)");
            }
        },

        teardown() {
            aborted = true;
            if (pc) {
                pc.onicecandidate = null;
                pc.ontrack = null;
                pc.onconnectionstatechange = null;
                try {
                    pc.close();
                } catch (_) {}
                pc = null;
            }
            pendingIce = [];
            isOfferer = false;
            if (localStream) {
                localStream.getTracks().forEach((t) => t.stop());
                localStream = null;
            }
        },
        destroy() {
            window.removeEventListener("socket:connected", rebind);
            window.removeEventListener("socket:reconnected", rebind);
            this.teardown();
        },
    };
}
